import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Shared pieces of the public site.

export function useReducedMotion() {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

export function Arrow({ size = 14, color = "currentColor" }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.8">
      <path d="M3 7h8M8 4l3 3-3 3" />
    </svg>
  );
}

export function CarIcon({ size = 22 }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="1.8">
      <circle cx="7" cy="16" r="3" />
      <circle cx="17" cy="16" r="3" />
      <path d="M4 13l3-5h8l3 5M10 8V5" />
    </svg>
  );
}

// Top-down car, centred on 0,0 and facing +x.
export function CarSprite({ flame = false }) {
  return (
    <>
      {flame && (
        <>
          <polygon className="flame" points="-30,-6 -30,6 -58,0" fill="#FFC83D" />
          <polygon className="flame" points="-30,-3 -30,3 -46,0" fill="#FF3D3D" />
        </>
      )}
      <rect x="-22" y="-17" width="12" height="7" rx="2" fill="#0B0C0F" />
      <rect x="-22" y="10" width="12" height="7" rx="2" fill="#0B0C0F" />
      <rect x="10" y="-17" width="12" height="7" rx="2" fill="#0B0C0F" />
      <rect x="10" y="10" width="12" height="7" rx="2" fill="#0B0C0F" />
      <rect x="-31" y="-13" width="6" height="26" rx="2" fill="#15171C" />
      <rect x="-25" y="-11" width="50" height="22" rx="8" style={{ fill: "var(--blue)" }} />
      <rect x="-25" y="-3" width="50" height="6" fill="#FFC83D" />
      <rect x="-4" y="-8" width="15" height="16" rx="5" fill="#15171C" />
      <rect x="22" y="-9" width="6" height="18" rx="2" fill="#15171C" />
    </>
  );
}

export function StartLights() {
  return (
    <svg role="img" aria-label="Start lights" width="118" height="22" viewBox="0 0 118 22">
      <rect width="118" height="22" rx="11" fill="#0E1014" />
      {[15, 37, 59, 81, 103].map((cx, i) => (
        <circle key={cx} className={`l${i + 1}`} cx={cx} cy="11" r="6" fill="#2A2D35" />
      ))}
    </svg>
  );
}

export function Gauge({ pct, value, label, color, delay }) {
  const p = Math.max(0, Math.min(1, pct));
  return (
    <div className="gauge rise" style={{ animationDelay: delay }}>
      <svg aria-hidden="true" width="140" height="92" viewBox="0 0 140 92">
        <path d="M15 80 A55 55 0 0 1 125 80" fill="none" stroke="#ECE8DD" strokeWidth="10" strokeLinecap="round" />
        <path
          className="arc"
          d="M15 80 A55 55 0 0 1 125 80"
          fill="none"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray="173"
          style={{ stroke: color, strokeDashoffset: Math.round(173 * (1 - p)) }}
        />
        <g className="ndlw">
          <line
            className="ndl"
            x1="70" y1="80" x2="70" y2="36"
            stroke="#15171C" strokeWidth="3" strokeLinecap="round"
            style={{ transform: `rotate(${Math.round(-90 + 180 * p)}deg)` }}
          />
        </g>
        <circle cx="70" cy="80" r="6" fill="#15171C" />
      </svg>
      <span className="gauge-v">{value}</span>
      <span className="gauge-l">{label}</span>
    </div>
  );
}

export function Tape({ height = 14 }) {
  return <div aria-hidden="true" className="tape" style={{ height }} />;
}

export function Wheel({ size = 64 }) {
  return (
    <svg className="wheel" aria-hidden="true" width={size} height={size} viewBox="0 0 64 64">
      <circle cx="32" cy="32" r="30" fill="#0B0C0F" />
      <circle cx="32" cy="32" r="18" fill="#2C3039" />
      <g stroke="#FFC83D" strokeWidth="3">
        <path d="M32 16v32M16 32h32M21 21l22 22M43 21L21 43" />
      </g>
      <circle cx="32" cy="32" r="5" style={{ fill: "var(--blue)" }} />
    </svg>
  );
}

export function Transmitter() {
  return (
    <svg className="bob" role="img" aria-label="A remote control with moving control sticks" viewBox="0 0 360 300" style={{ width: "100%", maxWidth: 440, justifySelf: "center", height: "auto" }}>
      <line x1="270" y1="70" x2="300" y2="6" stroke="#15171C" strokeWidth="6" strokeLinecap="round" />
      <circle className="led-b" cx="300" cy="6" r="6" fill="#FF3D3D" />
      <rect x="20" y="60" width="320" height="200" rx="60" fill="#15171C" />
      <rect x="40" y="80" width="280" height="160" rx="44" fill="#22252D" />
      <rect x="130" y="96" width="100" height="44" rx="8" fill="#9FD9A8" />
      <text x="180" y="116" textAnchor="middle" fontFamily="Geist Mono, monospace" fontSize="12" fontWeight="600" fill="#15171C">CH08 2.4G</text>
      <text x="180" y="132" textAnchor="middle" fontFamily="Geist Mono, monospace" fontSize="10" fill="#15171C">BIND ▮▮▮▮</text>
      <circle cx="95" cy="180" r="38" fill="#15171C" />
      <circle cx="265" cy="180" r="38" fill="#15171C" />
      <g className="stl"><circle cx="95" cy="180" r="16" fill="#3A3F4B" /><circle cx="95" cy="180" r="10" fill="#FFC83D" /></g>
      <g className="str"><circle cx="265" cy="180" r="16" fill="#3A3F4B" /><circle cx="265" cy="180" r="10" fill="#FFC83D" /></g>
      <rect x="160" y="160" width="40" height="12" rx="6" fill="#3A3F4B" />
      <rect x="160" y="182" width="40" height="12" rx="6" fill="#3A3F4B" />
      <circle cx="180" cy="222" r="7" fill="#3DDC84" />
    </svg>
  );
}

const BARCODE = [3, 1, 4, 2, 1, 5, 2, 1, 3, 2, 4, 1, 3, 2, 5, 1, 3, 2, 4, 1, 3, 2, 5, 1, 3, 2, 4, 1, 3, 2, 5, 1, 3, 2, 4, 1, 3, 2, 5, 1, 4];

export function Barcode() {
  let x = 0;
  const bars = BARCODE.map((w, i) => {
    const r = <rect key={i} x={x} width={w} height="28" />;
    x += w + 3;
    return r;
  });
  return (
    <svg aria-hidden="true" height="28" viewBox={`0 0 ${x} 28`} preserveAspectRatio="none" style={{ width: "100%" }}>
      <g fill="#15171C">{bars}</g>
    </svg>
  );
}

export function initials(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

export function ProfilePhoto({ name, src }) {
  const [failed, setFailed] = useState(false);
  const url = src || "/profile.jpeg";
  return (
    <div className="photo">
      {!failed ? (
        <img src={url} alt={`Portrait of ${name}`} onError={() => setFailed(true)} />
      ) : (
        <span className="disp" style={{ fontSize: 64, fontWeight: 900, color: "#3A4A8F" }}>{initials(name)}</span>
      )}
    </div>
  );
}

// Cover art for a project when the project has no cover image.
export function ProjectDiagram({ ink }) {
  return (
    <svg aria-hidden="true" width="240" height="130" viewBox="0 0 240 130" fill="none" stroke={ink} strokeWidth="1.5">
      <rect x="6" y="50" width="56" height="30" rx="6" />
      <rect x="92" y="10" width="56" height="30" rx="6" />
      <rect x="92" y="90" width="56" height="30" rx="6" />
      <rect x="178" y="50" width="56" height="30" rx="6" fill="#FFC83D" />
      <path d="M62 65h14l16-40M62 65h14l16 40M148 25l16 40h14M148 105l16-40" strokeDasharray="4 4" />
      <circle cx="34" cy="65" r="4" fill={ink} />
    </svg>
  );
}

export const PROJECT_COLORS = [
  { bg: "#FFC83D", ink: "#15171C" },
  { bg: "#E8EEFF", ink: "#1E3BB3" },
  { bg: "#1C1F26", ink: "#F3F0E8" },
  { bg: "#DFF7E8", ink: "#0F6B43" },
  { bg: "#FFE1DE", ink: "#A32620" },
  { bg: "#ECE8DD", ink: "#15171C" },
];

export function projectSpecs(project, full = true) {
  const stack = project.tech_stack || [];
  const [m1, m2] = project.metrics || [];
  const rows = full
    ? [
        ["TECH", stack[0]],
        ["STACK", stack.slice(1, 3).join(" · ")],
        [m1?.label?.toUpperCase() || "RESULT", m1 && m1.value],
        [m2?.label?.toUpperCase() || "IMPACT", m2 && m2.value],
      ]
    : [
        ["TECH", stack.slice(0, 2).join(" · ")],
        [m1?.label?.toUpperCase() || "RESULT", m1 && m1.value],
      ];
  if (!m1) rows.push(["YEAR", project.year]);
  return rows.filter(([, v]) => v);
}

export function ProjectCard({ project, index, full = true }) {
  const { bg, ink } = PROJECT_COLORS[index % PROJECT_COLORS.length];
  const specs = projectSpecs(project, full);

  return (
    <Link to={`/projects/${project.id}`} className="project-card rv" data-testid={`project-card-${project.id}`}>
      <span className="project-card-cover" style={{ background: bg, color: ink }}>
        <span className="project-card-number" style={{ color: ink }}>PROJECT {String(index + 1).padStart(2, "0")}</span>
        {project.featured && <span className="project-card-badge" style={{ color: ink, background: bg }}>★ FEATURED</span>}
        {project.cover_image ? <img src={project.cover_image} alt="" /> : <ProjectDiagram ink={ink} />}
      </span>
      <span className="project-card-body">
        {project.category && <span className="mono" style={{ fontSize: 11, color: "#6B6A63", textTransform: "uppercase" }}>{project.category}</span>}
        <span className="project-card-title">{project.title}</span>
        <span style={{ fontSize: 14, lineHeight: 1.5, color: "#4A4F5A" }}>{project.summary}</span>
        {specs.length > 0 && (
          <span className="project-spec">
            {specs.map(([k, v]) => (
              <span key={k} style={{ display: "contents" }}>
                <span className="project-spec-key">{k}</span>
                <span>{v}</span>
              </span>
            ))}
          </span>
        )}
        <span className="project-card-foot">
          View project
          <span className="arrow-box"><Arrow /></span>
        </span>
      </span>
    </Link>
  );
}

export function PageHero({ kicker, title, accent, sub, aside, children, compact = false }) {
  return (
    <section className="bx" style={{ paddingTop: 16 }}>
      <div className="ph rise d1 padl">
        <div className="ph-row">
          <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 780, minWidth: 0 }}>
            <span className="mono" style={{ fontSize: 12, color: "#A3A8B4", textTransform: "uppercase" }}>{kicker}</span>
            <h1 className={`ph-h stretch${compact ? " ph-h-sm" : ""}`} style={{ animationDelay: ".2s" }}>
              {title} {accent && <span className="em-y">{accent}</span>}
            </h1>
            {sub && <p className="ph-sub rise d4">{sub}</p>}
            {children}
          </div>
          {aside}
        </div>
      </div>
    </section>
  );
}

export function FilterChips({ label, options, value, onChange }) {
  return (
    <div role="group" aria-label={label} style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map((o) => (
        <button key={o} type="button" className="opt chipbtn" aria-pressed={o === value} onClick={() => onChange(o)}>
          {o}
        </button>
      ))}
    </div>
  );
}

export function SignalBars({ height = 26 }) {
  return (
    <span aria-hidden="true" style={{ display: "flex", alignItems: "center", gap: 3, height }}>
      {[0, 0.15, 0.3, 0.45].map((d) => (
        <span key={d} className="wv2" style={{ width: 3, height, background: "var(--blue)", animationDelay: `${d}s` }} />
      ))}
    </span>
  );
}

export function CheckeredFlag({ size = 120, alt = "#FFC83D" }) {
  const cells = [];
  for (let r = 0; r < 3; r += 1) {
    for (let c = 0; c < 6; c += 1) {
      cells.push(<rect key={`${r}-${c}`} x={7 + c * 18} y={r * 18} width="18" height="18" fill={(r + c) % 2 ? alt : "#15171C"} />);
    }
  }
  return (
    <svg className="flag" aria-hidden="true" width={size} height={(size * 2) / 3} viewBox="0 0 120 80">
      <line x1="4" y1="0" x2="4" y2="80" stroke="#15171C" strokeWidth="5" />
      <g>{cells}</g>
    </svg>
  );
}

export function formatDate(v, opts = { month: "short", year: "numeric" }) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, opts);
}
